import Echo from "laravel-echo";
import Pusher from "pusher-js";

// Expose Pusher on window so laravel-echo can find the constructor.
// laravel-echo looks up window.Pusher at instantiation time.
declare global {
    interface Window {
        Pusher: typeof Pusher;
        Echo: Echo<"pusher">;
    }
}
window.Pusher = Pusher;

let echoInstance: Echo<"pusher"> | null = null;

/**
 * Lazy singleton Echo client backed by Pusher Channels.
 *
 * Authenticates private channel subscriptions with the same Bearer token
 * used for API calls (TF uses personal-access-token auth, not Sanctum SPA
 * cookies). `/broadcasting/auth` is routed through `auth:sanctum` on the
 * backend so the same token works end-to-end.
 */
export function getEcho(): Echo<"pusher"> {
    if (echoInstance) return echoInstance;

    const key = import.meta.env.VITE_PUSHER_APP_KEY as string | undefined;
    const cluster = import.meta.env.VITE_PUSHER_APP_CLUSTER as string | undefined;

    if (!key || !cluster) {
        throw new Error(
            "[Echo] VITE_PUSHER_APP_KEY / VITE_PUSHER_APP_CLUSTER are not set. " +
                "Realtime chat will not work until the Pusher credentials are configured."
        );
    }

    echoInstance = new Echo({
        broadcaster: "pusher",
        key,
        cluster,
        forceTLS: true,
        authEndpoint: `${import.meta.env.VITE_API_BASE_URL}/broadcasting/auth`,
        auth: {
            headers: {
                get Authorization() {
                    const token = localStorage.getItem("ACCESS_TOKEN");
                    return token ? `Bearer ${token}` : "";
                },
                Accept: "application/json",
            },
        },
    });

    window.Echo = echoInstance;
    return echoInstance;
}

/**
 * Quitte le canal temps réel d'un établissement. Appelé à la bascule : sinon on
 * resterait abonné à chaque hôtel visité et on recevrait ses messages.
 * Ne crée pas de connexion si aucune n'existe.
 */
export function leaveFacilityChannel(facilityId: number): void {
    echoInstance?.leave(`facility.${facilityId}`);
}

/**
 * Tear down the Echo connection. Call on logout or when the token rotates
 * so the next subscription re-authenticates with fresh credentials.
 */
export function disconnectEcho(): void {
    if (echoInstance) {
        echoInstance.disconnect();
        echoInstance = null;
    }
}
